import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Video } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { ar as arLocale } from "date-fns/locale";
import { Card, CardContent } from "@/components/ui/card";
import { StatusBadge } from "@/components/dashboard/status-badge";
import { DecisionBadge } from "@/components/interviews/decision";
import { FilterBar } from "@/components/interviews/filter-bar";
import { initials } from "@/lib/utils";
import { useDict, useLocale, fill } from "@/lib/i18n/context";
import type { InterviewStatus } from "@/lib/constants";
import { Badge } from "@/components/ui/badge";

type InterviewRow = {
  id: string;
  candidate_name: string | null;
  job_title: string | null;
  total_score: number | null;
  decision: string | null;
  status: InterviewStatus;
  created_at: string;
  partnership_id?: string | null;
  agency_company_name?: string | null;
  client_company_name?: string | null;
  viewer_is_owner?: boolean;
};

export function InterviewsPage() {
  const t = useDict();
  const { locale } = useLocale();
  const dateLocale = locale === "ar" ? arLocale : undefined;
  const [searchParams] = useSearchParams();
  const [rows, setRows] = useState<InterviewRow[] | null>(null);
  const [jobs, setJobs] = useState<{ id: string; title: string }[]>([]);
  const [hasFilters, setHasFilters] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const q = searchParams.get("q") || "";
  const status = searchParams.get("status") || "";
  const decision = searchParams.get("decision") || "";
  const job = searchParams.get("job") || "";
  const from = searchParams.get("from") || "";
  const to = searchParams.get("to") || "";
  const qs = searchParams.toString();

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    fetch(`/api/interviews${qs ? `?${qs}` : ""}`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to load");
        return res.json() as Promise<{
          rows: InterviewRow[];
          jobs: { id: string; title: string }[];
          hasFilters: boolean;
        }>;
      })
      .then((data) => {
        if (active) {
          setRows(data.rows);
          setJobs(data.jobs);
          setHasFilters(data.hasFilters);
        }
      })
      .catch(() => {
        if (active) setError("Failed to load");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [qs]);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        Loading…
      </div>
    );
  }
  if (error || !rows) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-muted">
        {error || "Failed to load"}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t.interviews.title}</h1>
        <p className="text-muted">{t.interviews.subtitle}</p>
      </div>

      <FilterBar
        basePath="/interviews"
        jobs={jobs}
        initial={{ q, status, decision, job, from, to }}
      />

      {rows.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center py-16 text-center">
            <Video className="h-10 w-10 text-muted/60" />
            <p className="mt-4 font-medium">
              {hasFilters ? t.interviews.noMatches : t.interviews.empty}
            </p>
            {!hasFilters && (
              <>
                <p className="text-sm text-muted">{t.interviews.emptyDesc}</p>
                <Link to="/candidates/new" className="mt-4 text-brand-600">
                  {t.interviews.inviteCandidate}
                </Link>
              </>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="divide-y divide-border pt-2">
            {rows.map((i) => (
              <Link
                key={i.id}
                to={`/interviews/${i.id}`}
                className="flex items-center gap-3 py-3 hover:bg-surface"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-100 text-sm font-semibold text-brand-700">
                  {initials(i.candidate_name ?? "?")}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">
                    {i.candidate_name ?? t.common.candidate}
                  </p>
                  <p className="truncate text-sm text-muted">
                    {i.job_title ?? t.common.generalInterview}
                  </p>
                </div>
                {i.partnership_id && (
                  <Badge tone="brand" className="hidden sm:inline-flex">
                    {i.viewer_is_owner && i.client_company_name
                      ? fill(t.partners.forClient, {
                          client: i.client_company_name,
                        })
                      : i.agency_company_name
                        ? fill(t.partners.viaAgency, {
                            agency: i.agency_company_name,
                          })
                        : t.partners.nav}
                  </Badge>
                )}
                {i.total_score != null && (
                  <span className="hidden text-sm font-semibold sm:block">
                    {i.total_score}/100
                  </span>
                )}
                <span className="hidden text-xs text-muted md:block">
                  {formatDistanceToNow(new Date(i.created_at), {
                    addSuffix: true,
                    locale: dateLocale,
                  })}
                </span>
                <DecisionBadge decision={i.decision} />
                <StatusBadge status={i.status} />
              </Link>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
